import type { SceneNode } from "../scene/model";
import { textData } from "./text";
import { curvedText } from "./text-deformation";
export interface BackgroundRemoval {
  enabled: boolean;
  color: string;
  sensitivity: number;
  softness: number;
  mode: "all" | "edge";
}
export interface ImageEdge {
  style: "none" | "torn" | "blur" | "fade" | "wave" | "zigzag" | "stamp";
  amount: number;
}
export interface NativeEffects {
  hue: number;
  saturation: number;
  blur: number;
  shadow: boolean;
  shadowStyle?: {
    color: string;
    alpha: number;
    blur: number;
    x: number;
    y: number;
    angle?: number;
  };
  background: BackgroundRemoval;
  edge: ImageEdge;
}
export const edgeStyles: [ImageEdge["style"], string][] = [
  ["none", "Без оформления"],
  ["torn", "Рваная бумага"],
  ["blur", "Размытие края"],
  ["wave", "Волны"],
  ["zigzag", "Зубчатый край"],
  ["stamp", "Почтовая марка"],
];
export const blendModes = [
  "source-over",
  "multiply",
  "screen",
  "overlay",
  "darken",
  "lighten",
  "color-dodge",
  "color-burn",
  "hard-light",
  "soft-light",
  "difference",
] as const;
export function effects(node: SceneNode): NativeEffects {
  return {
    hue: 0,
    saturation: 0,
    blur: 0,
    shadow: false,
    background: {
      enabled: false,
      color: "",
      sensitivity: 28,
      softness: 12,
      mode: "all",
    },
    edge: { style: "none", amount: 35 },
    ...node.effects,
  } as NativeEffects;
}
export function hasEffects(node: SceneNode) {
  const e = effects(node);
  return !!(
    e.hue ||
    e.saturation ||
    e.blur ||
    e.shadow ||
    e.background.enabled ||
    e.edge.style !== "none" ||
    node.geometry.warpCorners ||
    (node.kind === "text" && curvedText(textData(node))) ||
    node.interaction.textGradient ||
    node.interaction.textAlpha !== undefined ||
    node.text.colorGradient ||
    node.text.strokeGradient ||
    (node.kind === "text" &&
      Object.values(
        node.text.styles as Record<string, { colorGradient?: unknown }>,
      ).some((s) => s.colorGradient)) ||
    node.appearance.fillGradient ||
    node.appearance.strokeGradient ||
    node.appearance.fillAlpha !== undefined ||
    node.appearance.strokeAlpha !== undefined
  );
}
export function assertEffects(node: SceneNode) {
  const e = effects(node),
    finite = (v: unknown, min: number, max: number) =>
      typeof v === "number" && Number.isFinite(v) && v >= min && v <= max;
  if (
    Object.keys(node.effects).some(
      (k) =>
        ![
          "hue",
          "saturation",
          "blur",
          "shadow",
          "shadowStyle",
          "background",
          "edge",
        ].includes(k),
    ) ||
    (e.shadowStyle !== undefined &&
      (!e.shadowStyle ||
        !/^#[\da-f]{6}$/i.test(e.shadowStyle.color) ||
        !finite(e.shadowStyle.alpha, 0, 1) ||
        !finite(e.shadowStyle.blur, 0, 200) ||
        !finite(e.shadowStyle.x, -1000, 1000) ||
        !finite(e.shadowStyle.y, -1000, 1000) ||
        (e.shadowStyle.angle !== undefined &&
          !finite(e.shadowStyle.angle, 0, 360)))) ||
    !finite(e.hue, -180, 180) ||
    !finite(e.saturation, -100, 100) ||
    !finite(e.blur, 0, 100) ||
    typeof e.shadow !== "boolean" ||
    !e.background ||
    typeof e.background.enabled !== "boolean" ||
    !/^$|^#[\da-f]{6}$/i.test(e.background.color) ||
    !finite(e.background.sensitivity, 0, 100) ||
    !finite(e.background.softness, 0, 100) ||
    !["all", "edge"].includes(e.background.mode) ||
    !e.edge ||
    !finite(e.edge.amount, 1, 150) ||
    ![...edgeStyles.map((s) => s[0]), "fade"].includes(e.edge.style)
  )
    throw new Error("Повреждены настройки эффектов.");
}
export function changeEffect(
  nodes: readonly SceneNode[],
  key: keyof NativeEffects,
  value: NativeEffects[keyof NativeEffects],
) {
  return nodes.map((node) => {
    const changed = { ...node, effects: { ...node.effects, [key]: value } };
    assertEffects(changed);
    return changed;
  });
}
